import { AnyAction, createDraftSafeSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ActionsObservable, combineEpics } from 'redux-observable';
import { of } from 'rxjs';
import { mergeMap, catchError, filter } from 'rxjs/operators';

import { api } from '~constants/api';
import { extractPaginationValues, getPaginationQueryParams } from '~features/page/pageUtils';
import { http } from '~services/http';
import { getResponseError } from '~utils/response';

import { actions as notificationsActions } from '../notifications/notificationsSlice';
import { actions as pageActions } from '../page/pageSlice';
import { actions as sharedActions } from '../sharedSlice';

import { EventTrigger, PaginationRequestPayload, PaginationResponse, ResponseError } from '~models';

export interface EventTriggersState {
  data: EventTrigger[] | null;
  loading: {
    list: boolean;
    update: boolean;
    delete: boolean;
  };
  status: 'updated' | 'deleted' | null;
  error?: ResponseError | null;
}

export const initialState: EventTriggersState = {
  data: null,
  loading: {
    list: false,
    update: false,
    delete: false,
  },
  status: null,
  error: null,
};

export const { name, reducer, actions } = createSlice({
  name: 'eventTrigger',
  initialState,
  reducers: {
    // Fetch event triggers
    fetchEventTriggersInit(state, payload: PayloadAction<PaginationRequestPayload>) {
      state.loading.list = true;
    },
    fetchEventTriggersSuccess(state, { payload }: PayloadAction<PaginationResponse<EventTrigger>>) {
      state.loading.list = false;
      state.data = payload.content;
    },
    fetchEventTriggersFailed(state, action: PayloadAction<ResponseError>) {
      state.loading.list = false;
      state.error = action.payload;
    },
    deleteEventTriggerInit(
      state,
      {
        payload,
      }: PayloadAction<
        PaginationRequestPayload & {
          eventId: number;
        }
      >
    ) {
      state.loading.delete = true;
      state.status = null;
    },

    deleteEventTriggerSuccess(state) {
      state.loading.delete = false;
      state.status = 'deleted';
    },

    deleteEventTriggerFailed(state, { payload }: PayloadAction<ResponseError>) {
      state.loading.delete = false;
      state.error = payload;
    },

    addEventTriggerInit(
      state,
      {
        payload,
      }: PayloadAction<
        PaginationRequestPayload & {
          values: Partial<EventTrigger>;
        }
      >
    ) {
      state.loading.update = true;
    },

    addEventTriggerSuccess(state) {
      state.loading.update = false;
    },

    addEventTriggerFailed(state, { payload }: PayloadAction<ResponseError>) {
      state.loading.update = false;
      state.error = payload;
    },

    updateEventTriggerInit(
      state,
      {
        payload,
      }: PayloadAction<
        PaginationRequestPayload & {
          eventId: number;
          values: Partial<EventTrigger>;
        }
      >
    ) {
      state.loading.update = true;
      state.status = null;
    },

    updateEventTriggerSuccess(state) {
      state.loading.update = false;
      state.status = 'updated';
    },

    updateEventTriggerFailed(state, { payload }: PayloadAction<ResponseError>) {
      state.loading.update = false;
      state.error = payload;
    },

    resetStatus: state => {
      state.status = null;
    },
  },
  extraReducers: {
    [sharedActions.reset.toString()]: state => Object.assign(state, initialState),
  },
});

const getEventTriggersState = (state: AES.RootState) => state.eventTrigger;

export const selectors = {
  getEventTriggersState,
  getEventTriggersData: createDraftSafeSelector(getEventTriggersState, state => state.data),
  getLoaders: createDraftSafeSelector(getEventTriggersState, state => state.loading),
  getStatus: createDraftSafeSelector(getEventTriggersState, state => state.status),
  getError: createDraftSafeSelector(getEventTriggersState, state => state.error),
};

const fetchEventTriggers = (actions$: ActionsObservable<AnyAction>) =>
  actions$.pipe(
    filter(actions.fetchEventTriggersInit.match),
    mergeMap(({ payload }) => {
      const searchParams = getPaginationQueryParams(payload);

      return http.getJSON<PaginationResponse<EventTrigger>>(api.eventTriggers.all(searchParams)).pipe(
        mergeMap(res =>
          of(actions.fetchEventTriggersSuccess(res), pageActions.setPagePagination(extractPaginationValues(res)))
        ),
        catchError(err => of(actions.fetchEventTriggersFailed(getResponseError(err))))
      );
    })
  );

const deleteEventTriggerEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.deleteEventTriggerInit.match),
    mergeMap(({ payload: { eventId, pagination } }) =>
      http.delete(api.eventTriggers.byId(eventId)).pipe(
        mergeMap(() =>
          of(
            actions.deleteEventTriggerSuccess(),
            actions.fetchEventTriggersInit({ pagination }),
            notificationsActions.enqueue({
              message: 'Event Trigger has been deleted',
              options: { variant: 'success' },
            })
          )
        ),
        catchError(err => of(actions.deleteEventTriggerFailed(getResponseError(err))))
      )
    )
  );

const addEventTriggerEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.addEventTriggerInit.match),
    mergeMap(({ payload: { values, pagination } }) =>
      http.post(api.eventTriggers.all(''), values).pipe(
        mergeMap(() =>
          of(
            actions.addEventTriggerSuccess(),
            actions.fetchEventTriggersInit({ pagination }),
            notificationsActions.enqueue({
              message: 'New Event Trigger added',
              options: { variant: 'success' },
            })
          )
        ),
        catchError(err => of(actions.addEventTriggerFailed(getResponseError(err))))
      )
    )
  );

const updateEventTriggerEpic = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    filter(actions.updateEventTriggerInit.match),
    mergeMap(({ payload: { eventId, values, pagination } }) =>
      http.patch(api.eventTriggers.byId(eventId), values).pipe(
        mergeMap(() =>
          of(
            actions.updateEventTriggerSuccess(),
            actions.fetchEventTriggersInit({ pagination }),
            notificationsActions.enqueue({
              message: 'Event Trigger has been updated',
              options: { variant: 'success' },
            })
          )
        ),
        catchError(err => of(actions.updateEventTriggerFailed(getResponseError(err))))
      )
    )
  );

export const epics = combineEpics(
  fetchEventTriggers,
  addEventTriggerEpic,
  deleteEventTriggerEpic,
  updateEventTriggerEpic
);

export const allEpics = {
  fetchEventTriggers,
  addEventTriggerEpic,
  deleteEventTriggerEpic,
  updateEventTriggerEpic,
};

declare global {
  namespace AES {
    export interface Actions {
      eventTrigger: typeof actions;
    }
    export interface RootState {
      eventTrigger: EventTriggersState;
    }
    export interface Selectors {
      eventTrigger: typeof selectors;
    }
  }
}
